import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useState } from "react";

import { useHistory, useLocation } from "react-router-dom";
import { AnalyticsResourceType, UserNotification } from "@doitintl/cmp-models";
import { Box, Button, CardHeader, Stack } from "@mui/material";
import Grid from "@mui/material/Grid2";

import { globalText, reportText } from "../../../../assets/texts";
import { reportTxt, systemLabelsTxt, templateCreateText } from "../../../../assets/texts/CloudAnalytics";
import { EditableResourceHeader } from "../../../../Components/EditableResourceHeader/EditableResourceHeader";
import useReportSaver from "../../../../Components/hooks/cloudAnalytics/reports/useReportSaver";
import { useHistoryWithFallbackUrl } from "../../../../Components/hooks/useHistoryWithFallbackUrl";
import { useReportSubscriptions } from "../../../../Components/NotificationSubscription/db";
import { SubscribeButton } from "../../../../Components/NotificationSubscription/SubscribeButton";
import SaveAsComponent from "../../../../Components/SaveComponents/SaveAsComponent";
import SaveComponent from "../../../../Components/SaveComponents/SaveComponent";
import { useSnackbar, useSuccessSnackbar } from "../../../../Components/SharedSnackbar/SharedSnackbar.context";
import { useAuthContext } from "../../../../Context/AuthContext";
import { useCustomerContext } from "../../../../Context/CustomerContext";
import { useIsFeatureEntitled } from "../../../../Context/TierProvider";
import { useDashboardsContext } from "../../../../Context/useDashboardsContext";
import { consoleErrorWithSentry } from "../../../../utils";
import { useFullScreen } from "../../../../utils/dialog";
import { useQuery } from "../../../../utils/useQuery";
import { updateReportConfigRequest } from "../../api";
import { useCloudAnalyticsContext } from "../../CloudAnalyticsContext";
import {
  CreateTemplateDataKind,
  useCreateTemplateContext,
  useReportConfig,
  useReportContext,
  useReportSaverContext,
  useReportTemplateContext,
} from "../../Context";
import { useStyles } from "../../ReportStyles";
import { type CloudAnalyticsHistoryState } from "../../types";
import { isEditor, isOwner } from "../../utilities";
import { type PopoverAnchorEl } from "../types";
import { ReportPathQueryParams } from "../utils";
import ActionButtons from "./ActionButtons";
import CacheLastUpdated from "./CacheLastUpdated";
import SystemLabel from "./SystemLabel";
import AddToReports from "./templateButtons/AddToReports";
import SubmitForApproval from "./templateButtons/SubmitForApproval";
import TemplateButtons from "./templateButtons/TemplateButtons";

type Props = {
  removeReportFromCache: () => Promise<void>;
  handleRevert: () => void;
  isCachedReport: boolean;
  popoverAnchorEl: any;
  reportCacheRefreshedAt?: Date;
  reportError?: string;
  saveButtonDisabled: boolean;
  setGoogleDialogOpen: Dispatch<SetStateAction<boolean>>;
  setIsDeleting: Dispatch<SetStateAction<boolean>>;
  setPopoverAnchorEl: (anchorEl: PopoverAnchorEl | null) => void;
  setRightPanelOpen: Dispatch<SetStateAction<boolean>>;
  showRemoveFromCache: boolean;
  wasRun: boolean;
  isScheduledReport: boolean;
};

const ReportHeader = ({
  removeReportFromCache,
  handleRevert,
  isCachedReport,
  popoverAnchorEl,
  reportCacheRefreshedAt,
  reportError,
  saveButtonDisabled,
  setGoogleDialogOpen,
  setIsDeleting,
  setPopoverAnchorEl,
  setRightPanelOpen,
  showRemoveFromCache,
  wasRun,
  isScheduledReport,
}: Props) => {
  const location = useLocation<{ prevPage?: string; dashboardId?: string }>();
  const query = useQuery();
  const dashboardIdRef = location.state?.dashboardId ?? query.get("dashboardId");
  const isEntitledAnalyticsReports = useIsFeatureEntitled("analytics:reports");
  const classes: any = useStyles();
  const history = useHistory<CloudAnalyticsHistoryState>();
  const { customer } = useCustomerContext();
  const { currentUser, isDoitEmployee } = useAuthContext({ mustHaveUser: true });
  const { isMobile: smDown } = useFullScreen();
  const { reportConfig, dispatchReportConfig } = useReportConfig();
  const { handleSaveDefaultReportConfig } = useCloudAnalyticsContext();
  const { onOpen: showSharedSnackbar } = useSnackbar();
  const successSnackbar = useSuccessSnackbar();
  const { report } = useReportContext();
  const isCurrentUserEditor = !!(report?.data && isEditor(currentUser.email, report.data));
  const isCurrentUserOwner = !!(report?.data && isOwner(currentUser.email, report.data));
  const { onSaveReportHeader, prepareConfigFromDimension } = useReportSaver();
  const {
    reportSaver: { saved, hasUnsavedChanges },
    dispatchReportSaver,
  } = useReportSaverContext();
  const { dashboards, addWidgetToDashboard } = useDashboardsContext();
  const { createTemplateData, dispatchCreateTemplateData } = useCreateTemplateContext();
  const { isFavoriteReportTemplate, reportTemplateId } = useReportTemplateContext();
  const subscriptions = useReportSubscriptions({ reportRef: report.ref });

  const [addedToDashboard, setAddedToDashboard] = useState(false);
  const [localName, setLocalName] = useState(reportConfig.name);
  const [localDescription, setLocalDescription] = useState(reportConfig.description);

  useEffect(() => {
    setLocalName(reportConfig.name);
    setLocalDescription(reportConfig.description);
  }, [reportConfig.name, reportConfig.description]);

  const handleSaveDefaultConfig = useCallback(async () => {
    try {
      await handleSaveDefaultReportConfig(prepareConfigFromDimension());

      showSharedSnackbar({
        message: reportText.SAVE_DEFAULT_CONFIG_SUCCESS,
        variant: "success",
        autoHideDuration: 5000,
      });
    } catch (error) {
      consoleErrorWithSentry(error);
    }
  }, [handleSaveDefaultReportConfig, prepareConfigFromDimension, showSharedSnackbar]);

  const addReportToDashboard = useCallback(
    async (dashboardId: string, reportId: string) => {
      try {
        const reportName = `cloudReports::${customer.id}_${reportId}`;
        await addWidgetToDashboard(dashboardId, reportName, { width: 4 });
      } catch (error) {
        consoleErrorWithSentry(error);
      }
    },
    [addWidgetToDashboard, customer.id]
  );

  const handleSave = useCallback(
    async (name?: string) => {
      const reportId = await onSaveReportHeader(name, false);

      if (dashboardIdRef && !addedToDashboard) {
        addReportToDashboard(dashboardIdRef, reportId);
        setAddedToDashboard(true);
      }
    },
    [onSaveReportHeader, dashboardIdRef, addedToDashboard, addReportToDashboard]
  );

  const handleSaveAs = useCallback(
    async (name: string) => {
      await onSaveReportHeader(name, true);
    },
    [onSaveReportHeader]
  );

  let saveButtonTooltipText = "";
  if (!isCurrentUserEditor && report?.data.type === AnalyticsResourceType.CUSTOM) {
    saveButtonTooltipText = reportText.ONLY_OWNER;
  }

  if (!isCurrentUserEditor && report?.data.type === AnalyticsResourceType.PRESET) {
    saveButtonTooltipText = reportText.PRESET_NOEDIT;
  }

  if (isCurrentUserEditor && saved && !hasUnsavedChanges) {
    saveButtonTooltipText = reportText.ALL_CHANGES_ARE_SAVED;
  }

  const handleNavigateToDashboard = useCallback(() => {
    if (dashboardIdRef) {
      const foundDashboard = dashboards.find((d) => d.id === dashboardIdRef);
      if (foundDashboard) {
        history.push(`/customers/${customer.id}/dashboards/${foundDashboard.name}`);
      }
    }
  }, [customer.id, dashboardIdRef, dashboards, history]);

  const handleNameDescriptionChange = async (values: { name: string; description?: string }) => {
    const payload: { name?: string; description?: string } = {};
    if (values.name) {
      payload.name = values.name;
      setLocalName(values.name);
    }
    if (values.description !== undefined) {
      payload.description = values.description;
      setLocalDescription(values.description);
    }
    dispatchReportConfig({ payload });

    if (report && !report.data.draft) {
      try {
        const config = prepareConfigFromDimension();
        await updateReportConfigRequest(
          config,
          values.name ?? reportConfig.name,
          report.ref.id,
          values.description ?? reportConfig.description
        );
        dispatchReportSaver({
          payload: {
            hasUnsavedChanges: false,
            saved: true,
            initialConfig: config,
          },
        });
        successSnackbar(reportTxt.REPORT_SAVED_SUCCESSFULLY);
      } catch (error) {
        consoleErrorWithSentry(error);
      }
    }
  };

  useEffect(() => {
    if (isDoitEmployee && query.has(ReportPathQueryParams.createTemplate)) {
      dispatchCreateTemplateData({ payload: { isCreateTemplateOpen: true } });
    }
  }, [query, dispatchCreateTemplateData, isDoitEmployee]);

  const parentUrl = useMemo(() => `/customers/${customer.id}/analyze/reports`, [customer.id]);
  const handleBackButtonClicked = useHistoryWithFallbackUrl(parentUrl);

  return (
    <Box
      sx={{
        borderBottom: "1px solid",
        borderBottomColor: "general.divider",
      }}
    >
      <CardHeader
        className={classes.cardHeader}
        sx={{
          py: 2,
          pl: 2.5,
          pr: 3,
          "& .MuiCardHeader-action": {
            margin: 0,
            alignSelf: "center",
          },
          "& .MuiCardHeader-content": {
            minWidth: 0,
            overflow: "hidden",
          },
        }}
        title={
          <EditableResourceHeader
            data-cy="report-name"
            name={localName}
            description={localDescription}
            onSave={handleNameDescriptionChange}
            onBackButtonClicked={handleBackButtonClicked}
            disabled={!isCurrentUserEditor || createTemplateData.isCreateTemplateOpen}
            showTemplateChip={!!reportTemplateId}
            resourceName="report"
          />
        }
        action={
          !smDown &&
          report && (
            <Box
              sx={{
                mr: 1.5,
              }}
            >
              {isDoitEmployee && !reportTemplateId && createTemplateData.isCreateTemplateOpen ? (
                <Stack
                  direction="row"
                  sx={{
                    columnGap: 1.5,
                  }}
                >
                  <Button
                    data-cy="cancel-template-creation"
                    onClick={() => {
                      dispatchCreateTemplateData({ type: CreateTemplateDataKind.ON_CANCEL, payload: {} });
                    }}
                    variant="outlined"
                  >
                    {globalText.CANCEL}
                  </Button>
                  <SubmitForApproval
                    disabled={!createTemplateData.isValid}
                    tooltip={!createTemplateData.isValid ? templateCreateText.FILL_REQUIRED_FIELDS : ""}
                  />
                </Stack>
              ) : isDoitEmployee && reportTemplateId && query.has(ReportPathQueryParams.editTemplate) ? (
                <TemplateButtons />
              ) : (
                <Grid
                  container
                  spacing={2}
                  wrap="nowrap"
                  sx={{
                    alignItems: "center",
                  }}
                >
                  {isScheduledReport && (
                    <SystemLabel label={systemLabelsTxt.SCHEDULED.LABEL} tooltip={systemLabelsTxt.SCHEDULED.TOOLTIP} />
                  )}
                  {isCachedReport && <CacheLastUpdated reportCacheRefreshedAt={reportCacheRefreshedAt} />}
                  <ActionButtons
                    removeReportFromCache={removeReportFromCache}
                    disableShare={!reportConfig.name}
                    handleRevert={handleRevert}
                    handleSaveDefaultConfig={handleSaveDefaultConfig}
                    hasUnsavedChanges={hasUnsavedChanges}
                    isCurrentUserOwner={isCurrentUserOwner}
                    setIsDeleting={setIsDeleting}
                    popoverAnchorEl={popoverAnchorEl}
                    report={report}
                    reportError={reportError}
                    setRightPanelOpen={setRightPanelOpen}
                    setGoogleDialogOpen={setGoogleDialogOpen}
                    setPopoverAnchorEl={setPopoverAnchorEl}
                    showRemoveFromCache={showRemoveFromCache}
                    wasRun={wasRun}
                  />
                  <Grid container spacing={1} wrap="nowrap">
                    {!report.data.draft && report?.data?.type === AnalyticsResourceType.CUSTOM && (
                      <Grid sx={{ whiteSpace: "nowrap" }}>
                        <SubscribeButton
                          subscriptions={subscriptions}
                          subscriptionType={UserNotification.ReportSubscription}
                          subscriptionParent={{ ...report.data, ref: report.ref, id: report.ref.id }}
                        />
                      </Grid>
                    )}
                    {!report.data.draft && (
                      <Grid sx={{ whiteSpace: "nowrap" }}>
                        <SaveAsComponent
                          variant="outlined"
                          disabled={!isEntitledAnalyticsReports && !isDoitEmployee}
                          onSaveAs={handleSaveAs}
                          saveAsTitle={reportText.SAVE_NEW}
                          textFieldProps={{
                            helperText: reportText.SAVE_AS_HELPER_TEXT,
                            label: reportText.SAVE_AS_LABEL,
                          }}
                          successMessage={reportText.SUCCESSFULLY_SAVED}
                        />
                      </Grid>
                    )}
                    <Grid>
                      {reportTemplateId ? (
                        <AddToReports
                          disabled={!isFavoriteReportTemplate ? hasUnsavedChanges : false}
                          tooltip={
                            !isFavoriteReportTemplate && hasUnsavedChanges
                              ? reportTxt.ADD_TO_MY_REPORTS_CUSTOMER_TOOLTIP
                              : ""
                          }
                        />
                      ) : (
                        <SaveComponent
                          disabled={saveButtonDisabled}
                          onSave={handleSave}
                          tooltip={saveButtonTooltipText}
                          successMessage={
                            dashboardIdRef && !addedToDashboard ? (
                              <>
                                Report successfully saved and will appear on your dashboard soon.
                                <Button sx={{ ml: 2 }} variant="contained" onClick={handleNavigateToDashboard}>
                                  View dashboard
                                </Button>
                              </>
                            ) : (
                              reportTxt.REPORT_SAVED_SUCCESSFULLY
                            )
                          }
                          showDialog={!reportConfig.name}
                          saveDialogLabel={reportText.REPORT_NAME}
                        />
                      )}
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Box>
          )
        }
      />
    </Box>
  );
};

export default ReportHeader;
